import * as React from "react"
import {Card, Icon, SemanticCOLORS} from "semantic-ui-react"
import {PlaceSign} from "./PlaceSign"
import {BookingListProps, PlaceItem} from "./BookingsListTable"
import {AirportStopFragment, BookingPaidBy, FlightTypeEnum} from "../graphql/queries"
import {formatDollars} from "../model/charges"

type BookingStatus = "pending" | "amber" | "cancelled" | "done"

function statusIconColor(status: BookingStatus): SemanticCOLORS {
  switch (status) {
    case "pending":
      return "green"
    case "amber":
      return "orange"
    case "cancelled":
      return "red"
    case "done":
      return "pink"
  }
}

function Status({status}: {status: BookingStatus}) {
  return (
    <span>
      Status
      {"\u2003"}
      <Icon color={statusIconColor(status)} name="car" size="large" />
    </span>
  )
}

function CardExtra({status}: {status: BookingStatus}) {
  return (
    <Card.Content extra style={{color: "black"}}>
      <Status status={status} />
      <span className="right floated">
        <Icon color="grey" name="write" />
      </span>
    </Card.Content>
  )
}

function flightTypeAbbrev(type: FlightTypeEnum) {
  switch (type) {
    case FlightTypeEnum.Domestic:
      return "Dom"
    case FlightTypeEnum.International:
      return "Intl"
  }
}

function paidByAbbreviation(paidBy: BookingPaidBy): string {
  switch (paidBy) {
    case BookingPaidBy.not_specified:
      return ""
    case BookingPaidBy.on_account:
      return "Acc"
    case BookingPaidBy.registered_credit_card:
      return "RCC"
    case BookingPaidBy.other:
      return "Oth"
    case BookingPaidBy.paid_in_car_income:
      return "PIC"
    case BookingPaidBy.paid_in_car_non_income:
      return "PIC"
  }
}

export function paidByStyle(paidBy: BookingPaidBy | null) {
  switch (paidBy) {
    case null:
    case BookingPaidBy.not_specified:
    case BookingPaidBy.on_account:
    case BookingPaidBy.other:
      return {}
    case BookingPaidBy.registered_credit_card:
    case BookingPaidBy.paid_in_car_income:
    case BookingPaidBy.paid_in_car_non_income:
      return {color: "green"}
  }
}

export function priceSummary(nettPrice: number | null, paidBy: BookingPaidBy | null) {
  let content: string = ""
  if (nettPrice !== null) {
    content += "\u2003"
    content += formatDollars(nettPrice)
  }
  if (paidBy !== null) {
    content += "\u2003"
    content += paidByAbbreviation(paidBy)
  }
  return content
}

export function airportStopSummary(stop: AirportStopFragment) {
  return ` ${flightTypeAbbrev(stop.flightType)} ${stop.airlineCode}${stop.flightNumber}`
}

export function placeSummary(place: PlaceItem, airportStop: AirportStopFragment | null) {
  const prefix = place.nickname ? "" : place.placeName
  const suffix = airportStop ? airportStopSummary(airportStop) : ""
  return prefix + suffix
}

const cardStampFormat = "ddd D MMM [at] h:mm a"

export function BookingsListCards({onBooking, bookingItems}: BookingListProps) {
  return (
    <Card.Group stackable itemsPerRow={3}>
      {bookingItems.map(
        ({
          id,
          bookingNumber,
          bookingStamp,
          nettPrice,
          paidBy,
          isImminent,
          isComplete,
          isCancelled,
          driver,
          origin,
          destination,
          airportPickup,
          airportDropoff,
          passenger,
        }) => (
          <Card
            key={id}
            fluid
            onClick={() => onBooking(bookingNumber)}
            style={{
              zIndex: "auto", // Overrides "z-index: 5" Semantic UI rule for link cards
            }}
          >
            <Card.Content>
              <Card.Header>{bookingStamp.format(cardStampFormat)}</Card.Header>
              <Card.Meta>
                {passenger.name}
                <br />
                <span style={paidByStyle(paidBy)}>
                  {`#${bookingNumber}` + priceSummary(nettPrice, paidBy)}
                </span>
              </Card.Meta>
              <Card.Description>
                <PlaceSign
                  nickname={origin.nickname}
                  pointing={false}
                  type={origin.type}
                  category={origin.category}
                />
                {placeSummary(origin, airportPickup)}
                <br />
                <PlaceSign
                  nickname={destination.nickname}
                  pointing={false}
                  type={destination.type}
                  category={destination.category}
                />
                {placeSummary(destination, airportDropoff)}
              </Card.Description>
            </Card.Content>
            {isCancelled ? (
              <CardExtra status="cancelled" />
            ) : isComplete ? (
              <CardExtra status="done" />
            ) : driver !== null && isImminent ? (
              <CardExtra status="amber" />
            ) : (
              <CardExtra status="pending" />
            )}
          </Card>
        ),
      )}
    </Card.Group>
  )
}



// WEBPACK FOOTER //
// ./src/components/BookingsListCards.tsx